import { Capacitor } from "@capacitor/core";
import api from "./api";
import { enableNativePush, disableNativePush, nativePushPermission } from "./nativePush";

const isNative = Capacitor.isNativePlatform();

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function pushSupported() {
  if (isNative) return true; // Android + iOS both support push via native APNs/FCM
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function pushPermission() {
  if (isNative) return null; // async on native — use getPushStatus() instead
  return typeof Notification !== "undefined" ? Notification.permission : "denied";
}

/** Cross-platform current on/off state for the notification toggle UI. */
export async function getPushStatus() {
  if (!pushSupported()) return false;
  if (isNative) {
    const state = await nativePushPermission();
    return state === "granted";
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return !!sub && pushPermission() === "granted";
  } catch (e) {
    return false;
  }
}

export async function enablePush() {
  if (!pushSupported()) throw new Error("Push wird auf diesem Gerät nicht unterstützt.");
  if (isNative) return enableNativePush();

  const permission = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Benachrichtigungen wurden nicht erlaubt.");

  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/vapid-public-key");
  if (!data.publicKey) throw new Error("Server-Schlüssel fehlt.");

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }
  await api.post("/push/subscribe", sub.toJSON());
  return true;
}

export async function disablePush() {
  if (isNative) return disableNativePush();

  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    try {
      await api.post("/push/unsubscribe", sub.toJSON());
    } catch (e) {}
    await sub.unsubscribe();
  }
  return true;
}

export async function sendTestPush() {
  await api.post("/push/test");
}
